import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, fmtErr } from "../lib/api";
import { ICONS } from "../lib/constants";
import { Plus, PencilSimple } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const fieldCount = (p) => (p.sections || []).reduce((a, s) => a + (s.fields || []).length, 0);

export default function IndustryPacks() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [packs, setPacks] = useState([]);
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (!user) return;
    api.get("/industry-packs").then(({ data }) => setPacks(data)).catch((e) => toast.error(fmtErr(e)));
  }, [user]);

  return (
    <div className="p-6" data-testid="industry-packs-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-medium text-[var(--c-text)]">Industry Packs</h2>
          <p className="text-xs text-[var(--c-muted)] mt-0.5">{packs.length} packs · spec schemas, calculation modules, validation domains, drawings & BOM templates</p>
        </div>
        {isAdmin && (
          <Button data-testid="new-pack-btn" onClick={() => nav("/industry-packs/new")}
            className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-8 text-xs">
            <Plus size={14} className="mr-1" /> Create New Industry Pack
          </Button>
        )}
      </div>

      <div className="mt-4 border border-[var(--c-border)] rounded-sm bg-[var(--c-surface)] overflow-x-auto">
        <table className="w-full text-sm" data-testid="packs-table">
          <thead>
            <tr className="border-b border-[var(--c-border)]">
              {["Pack", "Status", "Spec Fields", "Calc Modules", "Drawings", "Standards", ""].map((h, i) => (
                <th key={i} className="text-left px-4 py-3 text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {packs.map((p) => {
              const Icon = ICONS[p.icon] || ICONS.puzzle;
              return (
                <tr key={p.key} data-testid={`pack-row-${p.key}`}
                  className="border-b border-[var(--c-border)]/60 last:border-0 hover:bg-[var(--c-border)]/25 transition-colors duration-150">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-6 h-6 rounded-sm flex items-center justify-center border"
                        style={{ backgroundColor: `color-mix(in srgb, ${p.color} 10%, transparent)`, borderColor: `color-mix(in srgb, ${p.color} 25%, transparent)` }}>
                        <Icon size={13} weight="fill" style={{ color: p.color }} />
                      </div>
                      <div>
                        <div className="text-xs font-medium text-[var(--c-text)]">{p.name}</div>
                        <div className="font-mono text-[10px] text-[var(--c-muted)]">{p.key}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span data-testid={`pack-status-${p.key}`} className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm border ${
                      p.status === "active" ? "bg-[var(--c-green)]/10 border-[var(--c-green)]/40 text-[var(--c-green)]" : "bg-[var(--c-amber)]/10 border-[var(--c-amber)]/40 text-[var(--c-amber)]"}`}>
                      {p.status || "draft"}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-[var(--c-text)]">{fieldCount(p)}</td>
                  <td className="px-4 py-3 font-mono text-xs text-[var(--c-text)]">{(p.calc_modules || []).length}</td>
                  <td className="px-4 py-3 font-mono text-xs text-[var(--c-text)]">{(p.drawing_types || []).length}</td>
                  <td className="px-4 py-3 text-xs text-[var(--c-muted)] max-w-48 truncate">{(p.standards || []).join(", ") || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    {isAdmin && (
                      <button data-testid={`edit-pack-${p.key}`} onClick={() => nav(`/industry-packs/${p.key}/edit`)}
                        className="inline-flex items-center gap-1 text-xs text-[var(--c-muted)] hover:text-[var(--c-accent)] transition-colors duration-150">
                        <PencilSimple size={13} /> Edit
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {packs.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-[var(--c-muted)]" data-testid="packs-empty">No industry packs.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
